import { normalizeString } from '@/utils/url';
import { IAmounts, IFiltros, IIdsRequeridos } from '../search/interfaces/IEstructuraPrincipal';
import { ICustomRangeValue } from './interfaces/ICustomRangeValue';
import {
	IFiltrosBase,
	IFiltrosBaseValues,
} from '../search/interfaces/IFiltrosBase';
import { UrlSlug } from '@toctoc/lista-seo-common';

/**
 * Builds a custom range filter, which is a filter that is not a preset match.
 * A non-empty match name (category) is required to generate a label.
 *
 * @param {ICustomRangeValue} params - The parameters for the custom range filter.
 * @returns The custom range filter.
 */
export const buildCustomRangeFilter = ({
	hasPresetMatch,
	queryParamFrom,
	queryParamTo,
	matchName,
}: ICustomRangeValue) => {
	if (matchName.length === 0) return [];

	const nQueryFrom = Number(queryParamFrom);
	const nQueryTo = Number(queryParamTo);

	if (
		!hasPresetMatch &&
		!isNaN(nQueryFrom) &&
		nQueryFrom >= 0 &&
		((!isNaN(nQueryTo) && nQueryFrom <= nQueryTo) || queryParamTo === 'x')
	) {
		const label = (
			nQueryFrom === nQueryTo
				? `${queryParamFrom} ${matchName?.toLowerCase() ?? ''}`
				: `${queryParamFrom} ${queryParamTo === 'x' ? 'o más' : `a ${queryParamTo}`} ${matchName?.toLowerCase() ?? ''}`
		).trim();

		return [
			{
				id: -1,
				label,
				value: [nQueryFrom, isNaN(nQueryTo) ? 'x' : nQueryTo],
			},
		];
	}
	return [];
};

/**
 * Performs a strict query comparison between the query parameters and the preset filter ranges.
 *
 * @param {IAmounts[]} ranges - The preset filter ranges.
 * @param {string} queryParamFrom - The beginning of the range.
 * @param {string} queryParamTo - The end of the range.
 * @returns {IAmounts[]} The filtered ranges that match the query parameters.
 */
export const strictQueryComparison = (
	ranges: IAmounts[],
	queryParamFrom: string,
	queryParamTo: string,
) => {
	const queryMin = Number(queryParamFrom);
	const queryMax = Number(queryParamTo);
	
	// console.log("🔬 [strictQueryComparison] Input:", JSON.stringify({
	// 	queryParamFrom, queryParamTo,
	// 	queryMin, queryMax,
	// 	rangesCount: ranges.length,
	// 	ranges: ranges.map(r => ({ id: r.id, value: r.value, label: r.label }))
	// }, null, 2));
	
	return ranges.filter((r) => {
		const [rangeMin, rangeMax] = r.value;
		const filterMin = Number(rangeMin);
		const filterMax = rangeMax !== 'x' ? Number(rangeMax) : 'x';
		
		const matches = (
			queryMin === filterMin &&
			(queryMax === filterMax || queryParamTo === filterMax)
		);
		
		// console.log("🔬 [strictQueryComparison] Checking range:", JSON.stringify({
		// 	rangeId: r.id,
		// 	rangeValue: r.value,
		// 	filterMin, filterMax,
		// 	queryMin, queryMax,
		// 	condition1: queryMin === filterMin,
		// 	condition2: queryMax === filterMax || queryParamTo === filterMax,
		// 	matches
		// }, null, 2));
		
		return matches;
	});
};

export const matchQueryWithValueList = (
	paramObject: { [key: string]: string | string[] | undefined },
	valueList: IFiltros[],
) => {
	// console.log("🚀 [matchQueryWithValueList] Starting with:", JSON.stringify({
	// 	paramObject,
	// 	availableFilters: valueList.map(f => ({ id: f.id, valuesCount: f.values?.length }))
	// }, null, 2));
	
	const matchedValues = [];
	for (const param in paramObject) {
		const queryParam = paramObject[param];
		if (!queryParam) continue;

		// Skip tracking parameters that are not filters
		if (param === 'o') {
			// console.log("⏭️ [matchQueryWithValueList] Skipping tracking param:", param);
			continue;
		}

		const match = valueList.find((filter) => filter.id === param) as IFiltros;
		if (!match) {
			// console.log("❌ [matchQueryWithValueList] No filter found for param:", param);
			continue;
		}
		
		// console.log("✅ [matchQueryWithValueList] Found filter:", JSON.stringify({
		// 	param,
		// 	queryParam,
		// 	filterId: match.id,
		// 	filterType: match.type,
		// 	valuesCount: match.values?.length
		// }, null, 2));

		// Handle both string and array parameters
		const isString = typeof queryParam === 'string';
		const parameterValues = isString ? [queryParam] : queryParam;
		
		// For checkbox filters, process each parameter value individually
		if (match.type === 'check' && Array.isArray(parameterValues)) {
			const matchWithValue: typeof match.values = [];
			
			// Process each parameter value
			parameterValues.forEach(singleParam => {
				const foundValues = match.values.filter((val) => {
					const { value } = val;
					const [firstVal] = value !== undefined ? value : [];
					return firstVal !== undefined && singleParam === firstVal + '';
				});
				
				// Add found values to the result
				foundValues.forEach(foundVal => {
					if (!matchWithValue.find(existing => existing.id === foundVal.id)) {
						matchWithValue.push(foundVal);
					}
				});
			});

			// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
			const { switch: switchVal, buttons, ...extractUsefulSelection } = match;
			const copyMatch: IFiltros & { unit?: string; label?: string } = extractUsefulSelection;

			if (matchWithValue.length > 0) {
				matchedValues.push({
					...copyMatch,
					values: matchWithValue,
				});
			}
		} else {
			// For non-checkbox filters, use the original logic
			const [queryParamFrom, queryParamToNullable] = isString
				? queryParam.split(',')
				: queryParam;
			// If it's an empty string, or undefined, repeat the first value to generate a singleton {x}.
			const queryParamTo = queryParamToNullable || queryParamFrom;

			const matchWithValue = match.values.filter((val) => {
				const { value } = val;
				const [firstVal, secondVal] = value !== undefined ? value : [];

				return (
					firstVal !== undefined &&
					queryParamFrom === firstVal + '' &&
					(secondVal === undefined || queryParamTo === secondVal + '')
				);
			});

			// Relevant for ranged values;
			let unitRange = undefined;
			let labelRange = undefined;
			const matchWithValueRange = match.values
				.filter((val) => {
					const { ranges } = val;
					// console.log("🔍 [matchQueryWithValueList] Checking value for ranges:", JSON.stringify({
					// 	filterId: match.id,
					// 	queryParamFrom, queryParamTo,
					// 	valueId: val.id,
					// 	valueLabel: val.label,
					// 	hasRanges: ranges !== undefined && ranges.length > 0,
					// 	ranges: ranges
					// }, null, 2));
					
					if (ranges !== undefined && ranges.length > 0) {
						const matchingRanges = strictQueryComparison(
							ranges,
							queryParamFrom,
							queryParamTo,
						);
						// console.log("🎯 [matchQueryWithValueList] strictQueryComparison result:", JSON.stringify({
						// 	filterId: match.id,
						// 	queryParamFrom, queryParamTo,
						// 	valueLabel: val.label,
						// 	matchingRanges: matchingRanges.length,
						// 	ranges: ranges
						// }, null, 2));
						return matchingRanges.length > 0;
					}
					return false;
				})
				.map((rangeVal) => {
					const { ranges: rangesToExtract } = rangeVal;
					if (rangesToExtract !== undefined && rangesToExtract.length > 0) {
						const matchingRanges = strictQueryComparison(
							rangesToExtract,
							queryParamFrom,
							queryParamTo,
						);

						return matchingRanges.map((rangeValues) => {
							const { id, label, value } = rangeValues;
							unitRange = rangeVal.unit;
							labelRange = rangeVal.label;
							const labelContainer: { label?: string } = {};
							if (label) labelContainer.label = label;

							return {
								id,
								value,
								...labelContainer,
							};
						});
					}
					return [];
				})
				.flat();

			// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
			const { switch: switchVal, buttons, ...extractUsefulSelection } = match;
			// Required by range values (._.)?
			const copyMatch: IFiltros & { unit?: string; label?: string } =
				extractUsefulSelection;
			if (unitRange !== undefined) {
				copyMatch.unit = unitRange;
			}

			if (labelRange !== undefined) {
				copyMatch.label = labelRange;
			}

			const hasPresetMatch =
				matchWithValue.length > 0 || matchWithValueRange.length > 0;
			
			// Only create custom range filter if there's no preset match and we want to allow custom ranges
			// For now, we'll disable custom range creation to match production behavior
			// const customRangeValue = buildCustomRangeFilter({
			// 	hasPresetMatch,
			// 	queryParamFrom,
			// 	queryParamTo,
			// 	matchName: match.name ?? '',
			// });

			// Only add the filter if we have a preset match (exists in filtrosBase)
			if (hasPresetMatch) {
				matchedValues.push({
					...copyMatch,
					values: [...matchWithValue, ...matchWithValueRange],
				});
			}
		}
	}

	return matchedValues;
};

const propertyTypeMappings: { [key: string]: string } = {
	casa: 'casa',
	departamento: 'departamento',
	oficina: 'oficina',
	'local-comercial': 'local-comercial',
	bodega: 'bodega',
	estacionamiento: 'estacionamiento',
	parcela: 'parcela',
	terreno: 'terreno',
	'campo-agricola': 'campo-agricola',
	industrial: 'terreno-industrial',
	vacacional: 'lugar-vacacional',
};

// Mapping between filter IDs and UrlSlug types
// Only filters that should appear as URL segments, not query parameters
const filterToUrlSlugMapping: { [key: string]: UrlSlug } = {
	concepto: UrlSlug.CONCEPT,
	'tipo-de-busqueda': UrlSlug.OPERATION_TYPE,
	'tipo-de-propiedad': UrlSlug.PROPERTY_TYPE,
	region: UrlSlug.REGION,
	comuna: UrlSlug.COMMUNE,
	barrio: UrlSlug.NEIGHBORHOOD,
	chain: UrlSlug.CHAIN,
	//'tipo-de-cliente': UrlSlug.CLIENT_TYPE,
	//'project-order': UrlSlug.PROJECT_ORDER,
	// Removed: precios, dormitorios, bannos, etc. - these should be query parameters
};

/**
 * Separates filters that should be part of the URL segments from those that should be query parameters
 * This function is crucial for the new URL slug functionality
 */
export const separateFiltersForUrlAndQuery = (
	filtrosBase: any[],
	urlSlug?: Array<{ idType: UrlSlug }>,
): { urlFilters: any[]; queryFilters: any[] } => {
	if (!urlSlug || urlSlug.length === 0) {
		// Fallback to old behavior - only basic filters in URL
		const urlFilters = filtrosBase.filter(
			(filtro) =>
				filtro.id === 'tipo-de-busqueda' ||
				filtro.id === 'tipo-de-propiedad' ||
				filtro.id === 'region' ||
				filtro.id === 'comuna',
		);
		const queryFilters = filtrosBase.filter(
			(filtro) =>
				filtro.id !== 'tipo-de-busqueda' &&
				filtro.id !== 'tipo-de-propiedad' &&
				filtro.id !== 'region' &&
				filtro.id !== 'comuna',
		);
		return { urlFilters, queryFilters };
	}

	// With urlSlug, use the mapping to determine what goes in URL vs query
	const urlFilters: any[] = [];
	const queryFilters: any[] = [];

	filtrosBase.forEach((filtro) => {
		if (filtro.id && filterToUrlSlugMapping[filtro.id] !== undefined) {
			// This filter should be part of URL segments
			urlFilters.push(filtro);
		} else {
			// This filter should be a query parameter
			queryFilters.push(filtro);
		}
	});

	return { urlFilters, queryFilters };
};

const constructFlexibleUrl = (
	filters: IFiltros[],
	urlSlug: Array<{ idType: UrlSlug }>,
) => {
	// console.log("🏗️ [constructFlexibleUrl] Input:", JSON.stringify({
	// 	filters: filters.map(f => ({ id: f.id, values: f.values })),
	// 	urlSlug
	// }, null, 2));

	// Create a map of UrlSlug type to filter value
	const urlSegmentValues: Record<number, string> = {};
	const params: string[] = [];

	// Process each filter
	filters.forEach((filtro) => {
		if (!filtro.id) return;

		const urlSlugType = filterToUrlSlugMapping[filtro.id];
		// console.log(`🔍 [constructFlexibleUrl] Processing filter ${filtro.id}:`, JSON.stringify({
		// 	filtroId: filtro.id,
		// 	urlSlugType,
		// 	isInMapping: urlSlugType !== undefined,
		// 	isDormitorios: filtro.id === 'dormitorios'
		// }, null, 2));

		if (typeof urlSlugType === 'number') {
			// This filter should be part of the URL segments
			filtro.values.forEach((detail) => {
				let segmentValue = '';

				if (filtro.id === 'tipo-de-busqueda') {
					segmentValue =
						detail.label?.toLowerCase() === 'comprar' ? 'venta' : 'arriendo';
				} else if (filtro.id === 'tipo-de-propiedad') {
					segmentValue =
						propertyTypeMappings[normalizeString(detail.label)] ||
						normalizeString(detail.label);
				} else if (filtro.id === 'region') {
					if (detail.label?.includes('Magallanes')) {
						segmentValue = 'magallanes';
					} else {
						segmentValue = normalizeString(detail.label);
					}
				} else if (filtro.id === 'comuna') {
					if (detail.label?.includes('Aguirre')) {
						segmentValue = 'p-aguirre-cerda';
					} else {
						segmentValue = normalizeString(detail.label);
					}
				} else if (filtro.id === 'barrio') {
					segmentValue = normalizeString(detail.label);
				} else if (detail.label) {
					segmentValue = normalizeString(detail.label);
				}

				if (segmentValue) {
					// console.log(`📍 [constructFlexibleUrl] Adding URL segment:`, JSON.stringify({
					// 	filtroId: filtro.id,
					// 	urlSlugType,
					// 	segmentValue
					// }, null, 2));
					urlSegmentValues[urlSlugType as number] = segmentValue;
				}
			});
		} else {
			// This filter should be a query parameter
			const values: string[] = [];
			filtro.values.forEach((detail) => {
				if (detail.value && detail.value.length > 0) {
					detail.value.forEach((value) => {
						if (value !== undefined && value !== null) {
							values.push(value.toString());
						}
					});
				}
			});

			if (values.length > 0) {
				// Handle checkbox filters differently - create separate parameters for each value
				if (filtro.type === 'check') {
					// For checkbox filters, create separate parameters: estado=1&estado=2
					values.forEach((value) => {
						// console.log(`✅ [constructFlexibleUrl] Adding checkbox param:`, JSON.stringify({
						// 	filtroId: filtro.id,
						// 	param: `${filtro.id}=${value}`
						// }, null, 2));
						params.push(`${filtro.id}=${value}`);
					});
				} else {
					// For other filter types, use comma-separated values: precios=1,2
					// console.log(`🔗 [constructFlexibleUrl] Adding query param:`, JSON.stringify({
					// 	filtroId: filtro.id,
					// 	param: `${filtro.id}=${values.join(',')}`
					// }, null, 2));
					params.push(`${filtro.id}=${values.join(',')}`);
				}
			}
		}
	});

	// console.log("🗺️ [constructFlexibleUrl] URL segment values:", JSON.stringify(urlSegmentValues, null, 2));
	// console.log("🔗 [constructFlexibleUrl] Query params:", JSON.stringify(params, null, 2));

	// Build URL segments according to urlSlug order with collapsible logic
	const segments: string[] = [];
	const hasRegion = false;

	urlSlug.forEach(({ idType }) => {
		const segmentValue = urlSegmentValues[idType];

		// // Track if we have region for neighborhood dependency
		// if (idType === UrlSlug.REGION && segmentValue) {
		// 	hasRegion = true;
		// }

		// // Special case: comuna only if we have region
		// if (idType === UrlSlug.COMMUNE && !hasRegion) {
		// 	return; // Skip comuna if no region
		// }

		// // Special case: neighborhood only if we have region
		// if (idType === UrlSlug.NEIGHBORHOOD && !hasRegion) {
		// 	return; // Skip neighborhood if no region
		// }

		if (segmentValue) {
			// console.log(`➕ [constructFlexibleUrl] Adding segment:`, JSON.stringify({
			// 	idType,
			// 	segmentValue,
			// 	isBedroomsType: idType === UrlSlug.BEDROOMS
			// }, null, 2));
			segments.push(segmentValue);
		}
	});

	// Construct final URL
	let url = '/' + segments.join('/');

	// Add query parameters
	let params_formated = '';
	params.forEach((param) => {
		if (param && param !== 'undefined' && !param.includes('undefined')) {
			params_formated += params_formated.length > 0 ? `&${param}` : `${param}`;
		}
	});

	// Preserve tracking parameters from current URL (like 'o' parameter)
	if (typeof window !== 'undefined') {
		const currentUrl = new URL(window.location.href);
		const trackingParams = ['o'];
		
		trackingParams.forEach(paramName => {
			const paramValue = currentUrl.searchParams.get(paramName);
			if (paramValue) {
				const trackingParam = `${paramName}=${paramValue}`;
				params_formated += params_formated.length > 0 ? `&${trackingParam}` : `${trackingParam}`;
			}
		});
	}

	if (params_formated.length > 0) {
		url = `${url}?${params_formated}`;
	}

	// console.log("🎉 [constructFlexibleUrl] Final URL:", JSON.stringify({
	// 	url,
	// 	params_formated,
	// 	segments
	// }, null, 2));

	return [url, params_formated];
};

/**
 * Converts query parameters to filters that should be added to the filter state
 * This is used when initializing filters from URL query parameters
 */
export const convertQueryParamsToFilters = (
	queryParams: { [key: string]: string | string[] | undefined },
	filtersAvailable: IFiltros[],
): IFiltros[] => {
	const queryFilters: IFiltros[] = [];

	Object.keys(queryParams).forEach((paramKey) => {
		const paramValue = queryParams[paramKey];
		if (!paramValue) return;

		// Find the filter in available filters
		const availableFilter = filtersAvailable.find((f) => f.id === paramKey);
		if (!availableFilter) return;

		// Convert parameter values to array
		const values = Array.isArray(paramValue) ? paramValue : [paramValue];
		const filterValues: typeof availableFilter.values = [];

		values.forEach((value) => {
			const valueParts = value.split(',');

			// Try to match with available filter values
			availableFilter.values.forEach((availableValue) => {
				if (availableValue.value) {
					const matches = valueParts.some(
						(part) =>
							availableValue.value?.includes(parseInt(part)) ||
							availableValue.value?.includes(part),
					);

					if (
						matches &&
						!filterValues.find((fv) => fv.id === availableValue.id)
					) {
						filterValues.push(availableValue);
					}
				}
			});
		});

		if (filterValues.length > 0) {
			queryFilters.push({
				...availableFilter,
				values: filterValues,
			});
		}
	});

	return queryFilters;
};

export const constructUrlFromFilters = (
	filters: IFiltros[],
	urlSlug?: Array<{ idType: UrlSlug }>,
) => {
	if (filters.length == 0) return;

	// Create default urlSlug configuration for legacy URLs (no urlSlug from backend)
	let effectiveUrlSlug = urlSlug;
	if (!urlSlug || !Array.isArray(urlSlug) || urlSlug.length === 0) {
		// For legacy URLs, always use flexible structure to handle query parameters properly
		effectiveUrlSlug = [
			{ idType: UrlSlug.CONCEPT },
			{ idType: UrlSlug.OPERATION_TYPE },
			{ idType: UrlSlug.CHAIN },
			{ idType: UrlSlug.PROPERTY_TYPE },
			{ idType: UrlSlug.REGION },
			{ idType: UrlSlug.COMMUNE },
			{ idType: UrlSlug.NEIGHBORHOOD },
			{ idType: UrlSlug.CLIENT_TYPE },
			{ idType: UrlSlug.PROJECT_ORDER },
			// Removed UrlSlug.PRICE - prices should be query parameters
		];
	}

	// If urlSlug is provided and has valid elements, use flexible structure
	if (
		effectiveUrlSlug &&
		Array.isArray(effectiveUrlSlug) &&
		effectiveUrlSlug.length > 0
	) {
		return constructFlexibleUrl(filters, effectiveUrlSlug);
	}

	// Fallback to rigid structure
	let filter_tof = '',
		filter_tpf = '',
		filter_region = '',
		filter_comuna = '',
		url = '';

	const params: string[] = [];

	filters.forEach((filtro) => {
		filtro.values.forEach((detail, i) => {
			const values: string[] = [];

			if (filtro.id === 'tipo-de-busqueda')
				filter_tof =
					detail.label.toLocaleLowerCase() === 'comprar' ? 'venta' : 'arriendo';

			if (filtro.id === 'tipo-de-propiedad') {
				filter_tpf = propertyTypeMappings[normalizeString(detail.label)];
			}

			if (filtro.id === 'region') {
				if (detail.label.includes('Magallanes')) filter_region = 'magallanes';
				else filter_region = normalizeString(detail.label);
			}

			if (filtro.id === 'comuna') {
				if (detail.label.includes('Aguirre')) filter_comuna = 'p-aguirre-cerda';
				else filter_comuna = normalizeString(detail.label);
			}

			detail.value?.forEach((value) => {
				if (value !== undefined && value !== null) {
					values.push(value.toString());
				}
			});

			if (
				filtro.id &&
				filtro.id !== 'tipo-de-busqueda' &&
				filtro.id !== 'tipo-de-propiedad' &&
				filtro.id !== 'region' &&
				filtro.id !== 'comuna' &&
				values.length > 0
			) {
				// Handle checkbox filters differently - create separate parameters for each value
				if (filtro.type === 'check') {
					// For checkbox filters, create separate parameters: estado=1&estado=2
					values.forEach((value) => {
						params.push(`${filtro.id}=${value}`);
					});
				} else {
					// For other filter types, use comma-separated values: precios=1,2
					params.push(`${filtro.id}=${values.join(',')}`);
				}
			}
		});
	});

	// out of foreach
	let params_formated = '';
	params.forEach((param) => {
		if (param && param !== 'undefined' && !param.includes('undefined')) {
			params_formated += params_formated.length > 0 ? `&${param}` : `${param}`;
		}
	});

	// Preserve tracking parameters from current URL (like 'o' parameter)
	if (typeof window !== 'undefined') {
		const currentUrl = new URL(window.location.href);
		const trackingParams = ['o'];
		
		trackingParams.forEach(paramName => {
			const paramValue = currentUrl.searchParams.get(paramName);
			if (paramValue) {
				const trackingParam = `${paramName}=${paramValue}`;
				params_formated += params_formated.length > 0 ? `&${trackingParam}` : `${trackingParam}`;
			}
		});
	}

	//assembling url
	//no region, no comuna
	if (filter_region.length == 0) url = `/${filter_tof}/${filter_tpf}`;

	//no comuna
	if (filter_comuna.length == 0 && url.length == 0)
		url = `/${filter_tof}/${filter_tpf}/${filter_region}`;

	//complete
	if (url.length == 0)
		url = `/${filter_tof}/${filter_tpf}/${filter_region}/${filter_comuna}`;

	//add params
	if (params_formated.length > 0) url = `${url}?${params_formated}`;

	return [url, params_formated];
};

/**
 * Converts IFiltrosBase to IFiltros format
 * This is needed because filtroBase comes from backend with a different structure
 * than what the frontend filter system expects
 */
export const convertFiltrosBaseToFiltros = (
	filtrosBase: IFiltrosBase[],
): IFiltros[] => {
	if (!filtrosBase || filtrosBase.length === 0) return [];

	return filtrosBase
		.filter(
			(filtroBase: IFiltrosBase) =>
				filtroBase && filtroBase.id && typeof filtroBase.id === 'string',
		) // Filter out invalid filters
		.map((filtroBase: IFiltrosBase) => {
			// Convert IFiltrosBaseValues to IValues format
			const convertedValues: any[] = [];
			let filterUnit: string | undefined;
			let filterLabel: string | undefined;

			filtroBase.values?.forEach((baseValue: IFiltrosBaseValues) => {
				// Check if this value has ranges (like dormitorios, bannos, precios)
				if (
					(baseValue as any).ranges &&
					Array.isArray((baseValue as any).ranges)
				) {
					// For price filters, capture unit and label from the base value
					if (filtroBase.id === 'precios' && (baseValue as any).unit) {
						filterUnit = (baseValue as any).unit;
						filterLabel = baseValue.label;
					}
					
					// For each range, create a separate value entry
					(baseValue as any).ranges.forEach((range: any) => {
						// Generate a descriptive label if the range doesn't have one
						let rangeLabel = range.label;
						if (!rangeLabel && range.value && Array.isArray(range.value) && range.value.length >= 2) {
							const [from, to] = range.value;
							const unit = baseValue.label || ''; // "UF", "$", etc.
							// console.log("",unit);
							
							
							// Format numbers with thousands separator for better readability
							const formatNumber = (num: number) => {
								return num.toLocaleString('es-CL');
							};
							
							if (to === 'x' || to === null || to === undefined) {
								rangeLabel = `Precios: ${unit} ${formatNumber(from)} o más`.trim();
							} else if (from === to) {
								rangeLabel = `Precios: ${unit} ${formatNumber(from)}`.trim();
							} else {
								rangeLabel = `Precios: ${unit} ${formatNumber(from)} a ${unit} ${formatNumber(to)}`.trim();
							}
						}
						
						convertedValues.push({
							id: range.id,
							label: rangeLabel || baseValue.label || '',
							value: range.value || [],
							...((baseValue as any).unit && { unit: (baseValue as any).unit }),
						});
					});
				} else {
					// Regular value without ranges
					convertedValues.push({
						id: baseValue.id,
						label: baseValue.label || '',
						value: baseValue.value || [],
					});
				}
			});

			// Debug logging for price filter
			// if (filtroBase.id === 'precios') {
			// 	console.log("🔄 [convertFiltrosBaseToFiltros] Processing precios filter:", JSON.stringify({
			// 		filtroBase: {
			// 			id: filtroBase.id,
			// 			type: filtroBase.type,
			// 			switch: (filtroBase as any).switch,
			// 			values: filtroBase.values
			// 		},
			// 		convertedValues: convertedValues,
			// 		filterUnit,
			// 		filterLabel
			// 	}, null, 2));
			// }

			// Convert IFiltrosBase to IFiltros format
			const convertedFilter: IFiltros = {
				id: filtroBase.id, // Already filtered to ensure this exists
				name: filtroBase.name || '',
				type: filtroBase.type || 'check', // Use the actual type from filtroBase
				values: convertedValues,
				selected: false, // Default value
				switch: false, // Default value
				buttons: [], // Default empty array
			};

			// Add unit and label to the main filter if extracted from baseValue
			if (filterUnit) {
				(convertedFilter as any).unit = filterUnit;
			}
			
			if (filterLabel) {
				(convertedFilter as any).label = filterLabel;
			}

			// Only add properties if they are not undefined (to avoid serialization issues)
			if (filtroBase.mainFilter !== undefined) {
				convertedFilter.mainFilter = filtroBase.mainFilter;
			}

			if (filtroBase.hasFilterName !== undefined) {
				convertedFilter.hasFilterName = filtroBase.hasFilterName;
			}

			// Add switch property if it exists
			if ((filtroBase as any).switch !== undefined) {
				convertedFilter.switch = (filtroBase as any).switch;
			}

			// Add hasFilterName property if it exists
			if ((filtroBase as any).hasFilterName !== undefined) {
				convertedFilter.hasFilterName = (filtroBase as any).hasFilterName;
			}

			return convertedFilter;
		});
};

/**
 * Removes dependent filters when parent filters are removed
 * Uses chainDependencyList to identify which filters should be cleaned up
 */
export const cleanDependentFilters = (
	currentFilters: IFiltros[],
	availableFilters: IFiltros[],
	previousFilters: IFiltros[]
): IFiltros[] => {
	// Find which filters were removed
	const removedFilterIds = previousFilters
		.filter(prevFilter => !currentFilters.find(currFilter => currFilter.id === prevFilter.id))
		.map(filter => filter.id);

	// console.log('🧹 [cleanDependentFilters] DEBUG:', JSON.stringify({
	// 	currentFilters: currentFilters.map(f => ({ id: f.id, values: f.values.map(v => v.label) })),
	// 	previousFilters: previousFilters.map(f => ({ id: f.id, values: f.values.map(v => v.label) })),
	// 	removedFilterIds
	// }, null, 2));

	if (removedFilterIds.length === 0) {
		// console.log('✅ [cleanDependentFilters] No filters removed, no cleanup needed');
		return currentFilters; // No filters removed, no cleanup needed
	}

	// Find which filters should be cleaned up based on chainDependencyList
	const filtersToRemove = new Set<string>();
	
	removedFilterIds.forEach(removedFilterId => {
		// Find the parent filter configuration
		const parentFilterConfig = availableFilters.find(f => f.id === removedFilterId);
		
		// console.log('🔍 [cleanDependentFilters] Checking removed filter:', removedFilterId, 'has chainDependencyList:', !!parentFilterConfig?.chainDependencyList);
		
		if (parentFilterConfig?.chainDependencyList) {
			// Add dependent filters to removal set
			parentFilterConfig.chainDependencyList.forEach(dependency => {
				// console.log('➕ [cleanDependentFilters] Adding dependent filter to removal:', dependency.id);
				filtersToRemove.add(dependency.id);
				
				// Recursively find nested dependencies
				const addNestedDependencies = (filterId: string) => {
					const filterConfig = availableFilters.find(f => f.id === filterId);
					if (filterConfig?.chainDependencyList) {
						filterConfig.chainDependencyList.forEach(dep => {
							// console.log('🔗 [cleanDependentFilters] Adding nested dependency:', dep.id);
							filtersToRemove.add(dep.id);
							addNestedDependencies(dep.id);
						});
					}
				};
				
				addNestedDependencies(dependency.id);
			});
		}
	});

	// console.log('🗑️ [cleanDependentFilters] Filters to remove:', JSON.stringify(Array.from(filtersToRemove), null, 2));

	// Remove dependent filters from current filters
	const cleanedFilters = currentFilters.filter(filter => 
		filter.id && !filtersToRemove.has(filter.id)
	);

	// console.log('✨ [cleanDependentFilters] Final cleaned filters:', JSON.stringify(cleanedFilters.map(f => ({ id: f.id, values: f.values.map(v => v.label) }))), null, 2);

	return cleanedFilters;
};

/**
 * Filters available filters to only show those whose dependencies are satisfied
 * A filter should only be shown if all filters it depends on have values selected
 */
export const filterVisibleFilters = (
	availableFilters: IFiltros[],
	selectedFilters: IFiltros[]
): IFiltros[] => {
	// console.log('🔍 [filterVisibleFilters] Input:', {
	// 	availableFilters: availableFilters.map(f => ({ id: f.id, dependsOn: f.dependsOn })),
	// 	selectedFilters: selectedFilters.map(f => ({ id: f.id, hasValues: f.values && f.values.length > 0, values: f.values }))
	// });

	return availableFilters.filter(filter => {
		// If filter has no dependsOn, it should always be shown
		if (!filter.dependsOn || filter.dependsOn.length === 0) {
			// console.log(`✅ [filterVisibleFilters] ${filter.id}: No dependencies, always visible`);
			return true;
		}

		// Check if all dependencies are satisfied (have selected values)
		const dependenciesSatisfied = filter.dependsOn.every(dependency => {
			const dependentFilterSelected = selectedFilters.find(sf => sf.id === dependency.id);
			const hasDependentValue = dependentFilterSelected && 
				dependentFilterSelected.values && 
				dependentFilterSelected.values.length > 0;
			
			// console.log(`🔗 [filterVisibleFilters] ${filter.id} depends on ${dependency.id}:`, {
			// 	found: !!dependentFilterSelected,
			// 	hasValues: hasDependentValue,
			// 	values: dependentFilterSelected?.values || 'not found'
			// });
			return hasDependentValue;
		});

		// console.log(`${dependenciesSatisfied ? '✅' : '❌'} [filterVisibleFilters] ${filter.id}: ${dependenciesSatisfied ? 'VISIBLE' : 'HIDDEN'}`);
		return dependenciesSatisfied;
	});
};

/**
 * Cleans filters that are not compatible with the current filter combination based on idsRequeridos
 * This function finds the most specific matching idsRequeridos entry and removes filters not in its allowed list
 */
export const cleanIncompatibleFilters = (
	currentFilters: IFiltros[],
	idsRequeridos: IIdsRequeridos[]
): IFiltros[] => {
	if (!currentFilters || currentFilters.length === 0 || !idsRequeridos || idsRequeridos.length === 0) {
		return currentFilters;
	}

	// Find the best matching idsRequeridos entry based on current filters
	let bestMatch: { values: string[] } | null = null;
	let maxMatches = 0;

	for (const req of idsRequeridos) {
		let matches = 0;
		let allKeyConditionsMet = true;

		// Check if all key conditions are met by current filters
		for (const keyItem of req.key) {
			if (!keyItem || !keyItem.id) continue;
			
			const currentFilter = currentFilters.find(filter => filter.id === keyItem.id);
			
			if (!currentFilter || !currentFilter.values || currentFilter.values.length === 0) {
				allKeyConditionsMet = false;
				break;
			}

			// Check if any of the current filter values match the required values
			let hasMatchingValue = false;
			if (keyItem.values && Array.isArray(keyItem.values)) {
				hasMatchingValue = currentFilter.values.some(filterValue => 
					keyItem.values.some((requiredValue: any) => 
						filterValue.value && requiredValue.value && 
						Array.isArray(requiredValue.value) &&
						requiredValue.value.some((reqVal: any) => 
							filterValue.value!.includes(reqVal)
						)
					)
				);
			}

			if (hasMatchingValue) {
				matches++;
			} else {
				allKeyConditionsMet = false;
				break;
			}
		}

		// If all conditions are met and this has more matches than previous best, use this one
		if (allKeyConditionsMet && matches > maxMatches) {
			bestMatch = req;
			maxMatches = matches;
		}
	}

	// If no matching configuration found, return filters as-is
	if (!bestMatch) {
		return currentFilters;
	}

	// Filter out incompatible filters based on the best match
	const compatibleFilters = currentFilters.filter(filter => {
		// Keep filter if it's in the allowed list
		return filter.id && bestMatch!.values.includes(filter.id);
	});

	return compatibleFilters;
};

